import api from '../api';
import { API_URL } from '../config';

const getApiErrorMessage = (error) => {
  const rawData = error?.response?.data;
  if (typeof rawData === 'string') {
    return rawData.trim();
  }

  if (rawData && typeof rawData === 'object') {
    return rawData.message || rawData.error || rawData.detail || error?.message || 'Request failed';
  }

  return error?.message || 'Request failed';
};

export async function getDepartments() {
  try {
    const response = await api.get(`${API_URL}/departments`);
    console.log('Raw Departments Data:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching departments:', error);
    throw error;
  }
}

export async function getAllCandidates() {
  try {
    const response = await api.get(`${API_URL}/candidates`);
    console.log('getAllCandidates response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching candidates:', error);
    throw error;
  }
}

export async function addCandidate(candidateData, imageFile, resumeFile) {
  try {
    const formData = new FormData();
    formData.append('candidate', new Blob([JSON.stringify(candidateData)], { type: 'application/json' }));
    if (imageFile) {
      formData.append('image', imageFile);
    }
    if (resumeFile) {
      formData.append('resume', resumeFile);
    }

    const response = await api.post(`${API_URL}/candidates`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    console.log('Add candidate successful:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error adding candidate:', error);
    throw new Error(getApiErrorMessage(error));
  }
}

export async function updateCandidate(id, candidateData, imageFile, resumeFile) {
  try {
    const formData = new FormData();
    formData.append('candidate', new Blob([JSON.stringify(candidateData)], { type: 'application/json' }));
    if (imageFile) {
      formData.append('image', imageFile);
    }
    if (resumeFile) {
      formData.append('resume', resumeFile);
    }

    const response = await api.put(`${API_URL}/candidates/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    console.log('Update candidate successful:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error updating candidate:', error);
    throw new Error(getApiErrorMessage(error));
  }
}

export async function updateCandidateStatus(id, status) {
  try {
    const response = await api.put(`${API_URL}/candidates/${id}/status`, { status });
    console.log('Update candidate status successful:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error updating candidate status:', error);
    throw error;
  }
}
export async function getCandidateStatusHistory(id, options = {}) {
  try {
    const response = await api.get(`${API_URL}/candidates/${id}/status-history`, {
      skipOutageRedirect: true,
      ...options
    });
    console.log('Candidate status history:', response.data);
    return response.data || [];
  } catch (error) {
    console.error(`Error fetching candidate status history for candidate ${id}:`, error);
    return [];
  }
}

export async function deleteCandidate(id) {
  try {
    const response = await api.delete(`${API_URL}/candidates/${id}`);

    if (response.status === 204) {
      console.log('Delete candidate successful: No content');
      return { success: true };
    }

    console.log('Delete candidate successful:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error deleting candidate:', error);
    throw error;
  }
}

export async function getCandidateImage(id) {
  try {
    const response = await api.get(`${API_URL}/candidates/${id}/image`, {
      responseType: 'blob'
    });

    const blob = response.data;
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('Error fetching candidate image:', error);
    throw error;
  }
}

export async function getCandidateResume(id) {
  try {
    const response = await api.get(`${API_URL}/candidates/${id}/resume`, {
      responseType: 'blob'
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching candidate resume:', error);
    throw error;
  }
}

export async function createOnboarding(candidateId) {
  try {
    const response = await api.post(`${API_URL}/onboarding`, { candidateId });
    console.log('Create onboarding successful:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error creating onboarding:', error);
    throw error;
  }
}

export async function deleteOnboarding(id) {
  try {
    const response = await api.delete(`${API_URL}/onboarding/${id}`);

    if (response.status === 204) {
      console.log('Delete onboarding successful: No content');
      return { success: true };
    }

    console.log('Delete onboarding successful:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error deleting onboarding:', error);
    throw error;
  }
}

export async function getInProgressOnboardings() {
  try {
    const response = await api.get(`${API_URL}/onboarding/in-progress`);
    console.log('getInProgressOnboardings response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching in-progress onboardings:', error);
    throw error;
  }
}

export async function getStartWorkingOnboardings() {
  try {
    const response = await api.get(`${API_URL}/onboarding/start-working`);
    console.log('getStartWorkingOnboardings response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching start-working onboardings:', error);
    throw error;
  }
}

export async function getCompletedOnboardings() {
  try {
    const response = await api.get(`${API_URL}/onboarding/completed`);
    console.log('getCompletedOnboardings response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching completed onboardings:', error);
    throw error;
  }
}

export async function updateOnboardingChecklist(id, updateDTO) {
  try {
    const response = await api.put(`${API_URL}/onboarding/${id}/checklist`, updateDTO);
    console.log('Update onboarding checklist successful:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error updating onboarding checklist:', error);
    throw error;
  }
}

export async function getMonthlyOnboardings(year, month) {
  try {
    const response = await api.get(`${API_URL}/onboarding/monthly`, {
      params: { year, month }
    });
    console.log('getMonthlyOnboardings response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching monthly onboardings:', error);
    throw error;
  }
}

export async function getMonthlyOffboardings(year, month) {
  try {
    const response = await api.get(`${API_URL}/offboarding/monthly`, {
      params: { year, month }
    });
    console.log('getMonthlyOffboardings response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching monthly offboardings:', error);
    throw error;
  }
}

export async function getInternshipOnboardings() {
  try {
    const response = await api.get(`${API_URL}/onboarding/internship`);
    return response.data;
  } catch (error) {
    throw new Error('Failed to fetch internship onboardings: ' + error.message);
  }
}

export async function getOffices() {
  try {
    const response = await api.get(`${API_URL}/offices`);
    console.log('getOffices response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching offices:', error);
    throw error;
  }
}
export async function getActiveCandidates() {
  try {
    const response = await api.get(`${API_URL}/candidates/ActiveCandidates`);
    console.log('getAllCandidates response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching candidates:', error);
    throw error;
  }
}

export async function getCandidateDetailsReport() {
  try {
    const response = await api.get(`${API_URL}/candidates/report`);
    console.log('getCandidateDetailsReport response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching candidate details report:', error);
    throw error;
  }
}

